import * as THREE from 'three'

export interface BaseRaycasterCallbacks {
  onHover?: (object: THREE.Object3D) => void
  onUnhover?: (object: THREE.Object3D) => void
  onClick?: (object: THREE.Object3D) => void
}

export abstract class BaseRaycaster<
  TCallbacks extends BaseRaycasterCallbacks = BaseRaycasterCallbacks
> {
  protected scene: THREE.Scene
  protected camera: THREE.Camera
  protected raycaster = new THREE.Raycaster()
  protected mouse = new THREE.Vector2()
  protected hoveredObjects: Record<string, THREE.Object3D> = {}

  protected callbacks: TCallbacks

  constructor(
    scene: THREE.Scene,
    camera: THREE.Camera,
    callbacks: TCallbacks
  ) {
    this.scene = scene
    this.camera = camera
    this.callbacks = callbacks
  }

  /* ------------------------------------------------------------------ */
  /* INTERNAL                                                           */
  /* ------------------------------------------------------------------ */

  protected setMouse(e: MouseEvent): void {
    this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1
    this.mouse.y = -(e.clientY / window.innerHeight) * 2 + 1
  }

  protected getIntersects(): THREE.Intersection[] {
    this.raycaster.setFromCamera(this.mouse, this.camera)
    return this.raycaster.intersectObjects(this.scene.children, true)
  }

  /**
   * Override this in subclass to define what is "interactive"
   */
  protected abstract isTarget(object: THREE.Object3D): boolean

  /**
   * Optional override: stop click propagation
   */
  protected shouldStopClick(_object: THREE.Object3D): boolean {
    return false
  }

  /* ------------------------------------------------------------------ */
  /* HOVER                                                              */
  /* ------------------------------------------------------------------ */

  handleHover(e: MouseEvent): void {
    e.preventDefault()
    this.setMouse(e)

    const intersects = this.getIntersects()
    const hoveredUuids = intersects.map(i => i.object.uuid)

    for (const { object } of intersects) {
      if (this.hoveredObjects[object.uuid]) continue

      this.hoveredObjects[object.uuid] = object

      if (this.isTarget(object)) {
        this.callbacks.onHover?.(object)
      }
    }

    for (const uuid of Object.keys(this.hoveredObjects)) {
      if (!hoveredUuids.includes(uuid)) {
        const obj = this.hoveredObjects[uuid]
        delete this.hoveredObjects[uuid]

        if (this.isTarget(obj)) {
          this.callbacks.onUnhover?.(obj)
        }
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* CLICK                                                              */
  /* ------------------------------------------------------------------ */

  handleClick(e: MouseEvent): void {
    e.preventDefault()
    this.setMouse(e)

    const seenNames = new Set<string>()

    for (const { object } of this.getIntersects()) {
      const name = object.name.toUpperCase()

      if (seenNames.has(name)) continue
      seenNames.add(name)

      if (this.isTarget(object)) {
        this.callbacks.onClick?.(object)
        break
      }

      if (this.shouldStopClick(object)) {
        break
      }
    }
  }
}
